import { DataTable } from '@/components/data-table/DataTable';
import { PageHeader } from '@/components/feedback/PageHeader';
import { FillPage } from '@/components/layout/PageContainer';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  buildLineDiff,
  LINE_CLASS,
  LINE_SIGN,
  MAX_DIFF_LINES,
  prettyJson,
} from '@/features/tts-flow/lib/diff-helper';
import { formatDate, formatDateTime } from '@/lib/utils/date';
import type { ColumnDef } from '@tanstack/react-table';
import type { LucideIcon } from 'lucide-react';
import { Code, FileDiff, Loader2, Pencil, Plus, RefreshCw, Repeat, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useCvcLogDetails, useCvcLogs } from '../api';
import type { ContinuousSummaryLogAction, CvcLogRow } from '../types';

const CVC_ACTION_META: Record<
  ContinuousSummaryLogAction,
  { label: string; icon: LucideIcon; className: string }
> = {
  create: {
    label: 'Created',
    icon: Plus,
    className: 'border-sky-500/25 bg-sky-500/10 text-sky-700 dark:text-sky-400',
  },
  update: {
    label: 'Updated',
    icon: Pencil,
    className: 'border-amber-500/25 bg-amber-500/10 text-amber-700 dark:text-amber-400',
  },
  delete: {
    label: 'Deleted',
    icon: Trash2,
    className: 'border-rose-500/25 bg-rose-500/10 text-rose-700 dark:text-rose-400',
  },
};

function ActionBadge({ action }: { action: ContinuousSummaryLogAction }) {
  const meta = CVC_ACTION_META[action];
  const Icon = meta.icon;
  return (
    <Badge variant="outline" className={`gap-1 font-normal ${meta.className}`}>
      <Icon className="h-3 w-3" />
      {meta.label}
    </Badge>
  );
}

function JsonPane({
  title,
  tone,
  json,
}: {
  title: string;
  tone: 'added' | 'removed';
  json: string;
}) {
  return (
    <div className="min-w-0 space-y-1.5">
      <span
        className={`text-xs font-medium ${
          tone === 'removed'
            ? 'text-rose-600 dark:text-rose-400'
            : 'text-emerald-600 dark:text-emerald-400'
        }`}
      >
        {title}
      </span>
      <pre className="max-h-[55vh] overflow-auto rounded-md border border-border bg-muted/40 p-3 font-mono text-xs leading-relaxed">
        {json}
      </pre>
    </div>
  );
}

function CvcChangeDialog({ row, onClose }: { row: CvcLogRow; onClose: () => void }) {
  const [mode, setMode] = useState<'unified' | 'split'>('unified');
  const details = useCvcLogDetails(row.id);

  const oldText = prettyJson(details.data?.oldData);
  const newText = prettyJson(details.data?.newData);
  const canDiff = row.action === 'update' && oldText !== '' && newText !== '';

  const diff = useMemo(() => {
    if (!canDiff) return null;
    if (oldText.split('\n').length + newText.split('\n').length > MAX_DIFF_LINES) return null;
    return buildLineDiff(oldText, newText);
  }, [canDiff, oldText, newText]);

  const added = diff?.filter((l) => l.type === 'added').length ?? 0;
  const removed = diff?.filter((l) => l.type === 'removed').length ?? 0;

  // Single-payload fallback: delete stores old, create stores new.
  const singleText = row.action === 'delete' ? oldText || newText : newText || oldText;
  const singleLabel =
    row.action === 'create'
      ? 'Created record'
      : row.action === 'delete'
        ? 'Record before deletion'
        : details.data?.newData != null
          ? 'Record after update'
          : 'Record before update';

  const body = (() => {
    if (details.isLoading) {
      return (
        <div className="flex h-40 items-center justify-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading change details…
        </div>
      );
    }
    if (details.isError) {
      return (
        <div className="flex h-40 flex-col items-center justify-center gap-2">
          <span className="text-sm text-destructive">Could not load details.</span>
          <Button size="sm" variant="outline" onClick={() => details.refetch()}>
            Retry
          </Button>
        </div>
      );
    }
    if (!canDiff && singleText === '') {
      return (
        <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
          No payload stored for this entry.
        </div>
      );
    }
    if (canDiff) {
      return diff && mode === 'unified' ? (
        <div className="max-h-[55vh] overflow-auto rounded-md border border-border bg-muted/30 font-mono text-xs leading-relaxed">
          {diff.map((line, idx) => (
            <div key={idx} className={`px-3 py-0.5 ${LINE_CLASS[line.type]}`}>
              <span className="select-none pr-2 opacity-60">{LINE_SIGN[line.type]}</span>
              <span className="whitespace-pre-wrap break-all">{line.text}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          <JsonPane title="Before" tone="removed" json={oldText} />
          <JsonPane title="After" tone="added" json={newText} />
        </div>
      );
    }
    return (
      <div className="space-y-2">
        <span className="text-xs font-medium text-muted-foreground">{singleLabel}</span>
        <pre className="max-h-[60vh] overflow-auto rounded-md border border-border bg-muted/40 p-3 font-mono text-xs leading-relaxed">
          {singleText}
        </pre>
      </div>
    );
  })();

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl lg:max-w-5xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ActionBadge action={row.action} />
            Change — log #{row.id}
          </DialogTitle>
          <DialogDescription>
            Campaign #{row.campaignId} · {formatDateTime(row.createdAt)} · user {row.userId ?? '—'}
          </DialogDescription>
        </DialogHeader>

        {canDiff && (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-xs text-muted-foreground">
              <span className="font-medium text-emerald-600 dark:text-emerald-400">+{added}</span>{' '}
              <span className="font-medium text-rose-600 dark:text-rose-400">−{removed}</span>{' '}
              line(s) changed
            </span>
            {diff && (
              <div className="flex items-center gap-0.5 rounded-md border border-border p-0.5">
                <Button
                  size="sm"
                  variant={mode === 'unified' ? 'secondary' : 'ghost'}
                  className="h-7 px-2.5 text-xs"
                  onClick={() => setMode('unified')}
                >
                  Diff
                </Button>
                <Button
                  size="sm"
                  variant={mode === 'split' ? 'secondary' : 'ghost'}
                  className="h-7 px-2.5 text-xs"
                  onClick={() => setMode('split')}
                >
                  Side by side
                </Button>
              </div>
            )}
          </div>
        )}

        {body}
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

type ActionFilter = 'all' | ContinuousSummaryLogAction;

export function CvcLogsPage() {
  const today = useMemo(() => new Date(), []);
  const [startDate, setStartDate] = useState<Date | undefined>(today);
  const [endDate, setEndDate] = useState<Date | undefined>(today);
  const [actionFilter, setActionFilter] = useState<ActionFilter>('all');

  const startStr = startDate ? formatDate(startDate) : '';
  const endStr = endDate ? formatDate(endDate) : '';

  const logs = useCvcLogs({
    startDate: startStr,
    endDate: endStr,
    action: actionFilter === 'all' ? undefined : actionFilter,
  });

  const rows = useMemo(() => [...(logs.data ?? [])].sort((a, b) => b.id - a.id), [logs.data]);

  const [viewing, setViewing] = useState<CvcLogRow | null>(null);

  const columns = useMemo<ColumnDef<CvcLogRow>[]>(
    () => [
      {
        accessorKey: 'id',
        header: 'ID',
        size: 90,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">{row.original.id}</span>
        ),
      },
      {
        accessorKey: 'campaignId',
        header: 'Campaign',
        cell: ({ row }) => (
          <span className="flex items-center gap-2 font-medium text-foreground">
            <Repeat className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="font-mono text-xs">#{row.original.campaignId}</span>
          </span>
        ),
      },
      {
        accessorKey: 'action',
        header: 'Action',
        size: 120,
        cell: ({ row }) => <ActionBadge action={row.original.action} />,
      },
      {
        accessorKey: 'userId',
        header: 'User',
        size: 110,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.userId ?? '—'}
          </span>
        ),
      },
      {
        accessorKey: 'createdAt',
        header: 'When',
        size: 190,
        cell: ({ row }) => formatDateTime(row.original.createdAt),
      },
      {
        id: 'change',
        header: 'Change',
        size: 110,
        enableSorting: false,
        cell: ({ row }) => {
          const isDiff = row.original.action === 'update';
          return (
            <Button size="sm" variant="outline" onClick={() => setViewing(row.original)}>
              {isDiff ? <FileDiff className="h-3.5 w-3.5" /> : <Code className="h-3.5 w-3.5" />}
              {isDiff ? 'Diff' : 'View'}
            </Button>
          );
        },
      },
    ],
    [],
  );

  return (
    <FillPage>
      <PageHeader
        title="Continuous Summary Change History"
        description="Audit trail for continuous campaign summary changes — creates, updates and deletes."
        actions={
          <Button variant="outline" onClick={() => logs.refetch()} disabled={logs.isFetching}>
            <RefreshCw className={logs.isFetching ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
            Refresh
          </Button>
        }
      />

      <div className="flex min-h-0 flex-1 flex-col gap-3">
        <div className="flex flex-wrap items-end gap-3 rounded-lg border border-border bg-card p-3 shadow-sm">
          <div className="space-y-1.5">
            <Label className="text-xs">Start date</Label>
            <DatePicker value={startDate} onChange={setStartDate} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">End date</Label>
            <DatePicker value={endDate} onChange={setEndDate} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Action</Label>
            <Select value={actionFilter} onValueChange={(v) => setActionFilter(v as ActionFilter)}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="All actions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All actions</SelectItem>
                <SelectItem value="create">Created</SelectItem>
                <SelectItem value="update">Updated</SelectItem>
                <SelectItem value="delete">Deleted</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <span className="ml-auto text-xs text-muted-foreground">
            {rows.length.toLocaleString()} {rows.length === 1 ? 'log entry' : 'log entries'}
          </span>
        </div>

        <div className="min-h-0 flex-1">
          <DataTable<CvcLogRow, unknown>
            tableId="cvc:logs:v1"
            columns={columns}
            data={rows}
            isLoading={logs.isLoading}
            searchPlaceholder="Search logs…"
            emptyTitle="No change history"
            emptyDescription="Continuous campaign summary changes in this date range will appear here."
            getRowId={(row) => String(row.id)}
            initialColumnPinning={{ right: ['change'] }}
            exportFilename="cvc-logs.csv"
            pageSize={25}
          />
        </div>
      </div>

      {viewing && (
        <CvcChangeDialog key={viewing.id} row={viewing} onClose={() => setViewing(null)} />
      )}
    </FillPage>
  );
}
